import { useEffect, useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'core/store/hooks';
import { H4, H5 } from 'app/componentsV2/Typography/Typography';
import Table from 'app/componentsV2/Table/Table';
import { columns } from '../../../Module/ModuleShare/columns';
import { ColumnDef } from '@tanstack/react-table';
import { DataTableType } from 'app/componentsV2/Table/types';
import Pagination from 'app/componentsV2/Pagination/Pagination';
import { usePagination } from 'core/hooks/usePagination';
import { selectPaginatedCourseProgress, selectCourseProgressName } from 'core/selectors/admin/courseProgress';
import { LoadingSpinner } from 'app/components/Loading/loadingspinner';
import { EntityTypes } from 'constants/entities';
import { ReportType } from 'core/slices/report';
import Typeahead, { TypeaheadOption } from 'app/componentsV2/Typeahead';
import { SearchType } from 'types/domain/search';
import { updateSearch, clearSearchLoading } from 'core/slices/search';
import Confirm from 'app/componentsV2/Confirm/Confirm';
import useConfirm from 'core/hooks/useConfirm';
import { useSave } from 'core/hooks/useSave';
import classNames from 'classnames';
import { Card } from 'app/componentsV2/Card';
import { useNavigate } from 'react-router-dom';
import { SECTIONS } from 'constants/sections';

interface ModuleShareUsersProps {
  courseNodeId: number;
  courseId: number;
  componentId: string;
  typeaheadComponentId: string;
  className?: string;
}

const CourseNodeShare: React.FC<ModuleShareUsersProps> = ({
  courseNodeId,
  courseId,
  componentId,
  typeaheadComponentId,
  className,
}) => {
  const dispatch = useAppDispatch();
  const saveCourseProgress = useSave(EntityTypes.COURSE_PROGRESS);
  const confirmProps = useConfirm();
  const navigate = useNavigate();

  // Get paginated course progress for current learners table
  const { paginatedProgress, totalRecords, currentPage, pageSize, loading } = useAppSelector(state =>
    selectPaginatedCourseProgress(state, componentId, courseNodeId),
  );

  // Get course progress list for typeahead search
  const courseProgressList = useAppSelector(selectCourseProgressName);

  // Get search state for typeahead
  const search = useAppSelector(state => state.core.search.components[typeaheadComponentId]);

  // Memoize report filter to prevent recreation on every render
  const reportFilter = useMemo(() => ({ courseNodeId: courseNodeId }), [courseNodeId]);

  // Pagination hook for current learners table
  const { fetchData, handlePageChange, handlePageSizeChange } = usePagination({
    entityType: EntityTypes.COURSE_PROGRESS,
    componentId: componentId,
    reportType: ReportType.COURSE_PROGRESS,
    parentEntityId: courseId,
    watchFilters: false,
    reportFilter: reportFilter,
  });

  // Load current learners on mount and when courseId changes
  useEffect(() => {
    if (courseId) {
      fetchData(0, pageSize).catch(err => console.error('Error loading current learners:', err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const onSearch = useCallback(
    term => {
      dispatch(
        updateSearch({
          term,
          componentId: typeaheadComponentId,
          reportType: ReportType.COURSE_PROGRESS,
          searchType: SearchType.COURSE_PROGRESS,
        }),
      );
    },
    [dispatch, typeaheadComponentId],
  );

  const onSelectUser = useCallback(
    ({ value: id }) =>
      confirmProps
        .confirm({
          title: 'Move this user',
          message: 'Are you sure you want to move this user to this module?',
          open: true,
        })
        .then(() => {
          saveCourseProgress(
            { id, courseNodeId: courseNodeId, resetCourseNode: true, generateToken: true },
            { mergeData: true },
          );
        }),
    [confirmProps, courseNodeId, saveCourseProgress],
  );

  return (
    <>
      <div className={classNames(className, ``)}>
        <div className="flex flex-col flex-1">
          <H5 className="my-5">Current learners</H5>

          {/* Loading State */}
          {loading && paginatedProgress.length === 0 && (
            <div className="flex justify-center items-center h-32">
              <LoadingSpinner />
            </div>
          )}

          {/* Data Table with Pagination */}
          {!loading || paginatedProgress.length > 0 ? (
            <>
              <Table
                onRowClick={row =>
                  navigate(
                    `/${SECTIONS.COURSE}/${courseId}/${SECTIONS.LEARNERS}/${SECTIONS.COURSE_PROGRESS}/${row.original.id}`,
                  )
                }
                className="mb-5"
                data={paginatedProgress}
                columns={columns as ColumnDef<DataTableType>[]}
              />
              <Pagination
                currentPage={currentPage}
                totalItems={totalRecords}
                pageSize={pageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                loading={loading}
                itemLabel="learners"
                className="mt-4 mb-6"
              />
            </>
          ) : null}
        </div>
        <div className="flex-1">
          <Card className="flex flex-col  my-20">
            <H5 className="my-5">Move learner to this module</H5>

            <Typeahead
              value={search?.searchTerm ?? ''}
              options={
                courseProgressList
                  .filter(cp => cp.courseNodeId !== courseNodeId)
                  .map(
                    cp =>
                      ({
                        label: cp.organisationUser.name || cp.organisationUser.email,
                        value: cp.id,
                      } as TypeaheadOption),
                  ) ?? []
              }
              onChange={onSearch}
              onClose={() => dispatch(clearSearchLoading({ componentId: typeaheadComponentId }))}
              loading={search?.loading}
              onSelect={onSelectUser}
              className="mb-20"
            />
          </Card>
        </div>
      </div>

      <Confirm {...confirmProps} />
    </>
  );
};

export default CourseNodeShare;
